/**
 * ProjectCompanyService - Phase 3 of 2026 Access Model
 *
 * Canonical service for managing project-company relationships
 * and enhanced project memberships with company context.
 *
 * Features:
 *   - Project company management
 *   - Company representative functions
 *   - PM authority boundary checks
 *   - Member queries with company context
 *
 * @see 20260110200000_ax1_project_company_memberships.sql
 */

import { supabase as supabaseClient } from '../lib/supabase';
import { IS_DEMO_MODE } from '../config/env';
import { captureError } from './MonitoringService';
import type {
  ProjectCompany,
  ProjectCompanyCreate,
  ProjectCompanyUpdate,
  ProjectCompanyWithDetails,
  ProjectMemberCompanyInfo,
  ProjectRole,
  ColleagueInviteResult
} from '../types/projectCompany';
import type { ProjectCompanyRelationshipType, LegacyProjectCompanyRelationshipValue } from '../types/company';
import { normalizeProjectCompanyRelationship } from '../types/company';
import {
  canDemoManageProject,
  getDemoAuthorityActor,
} from '../demo/demoAuthoritySession';
import {
  getDemoCompanyRepresentatives,
  getDemoProjectParticipant,
} from '../demo/demoProjectParticipantStore';
import * as DemoStore from '../demo/demoStore';

// Non-null assertion wrapper
const supabase = supabaseClient!;

// ─────────────────────────────────────────────────────────────────────────────
// Error Handling
// ─────────────────────────────────────────────────────────────────────────────

function parseError(error: Error | { message: string; code?: string }): string {
  const message = error.message || 'Unknown error';

  if (message.includes('FORBIDDEN')) {
    const reason = message.split('FORBIDDEN:')[1]?.trim() || 'Access denied';
    return reason;
  }
  if (message.includes('project_companies_unique')) {
    return 'This company is already associated with the project in this role';
  }
  if (message.includes('violates row-level security')) {
    return 'Permission denied for this operation';
  }

  return message;
}

function handleError(operation: string, error: unknown): never {
  const parsedMessage = parseError(error as Error);
  const fullMessage = `ProjectCompanyService.${operation}: ${parsedMessage}`;

  captureError(error as Error, {
    tags: { service: 'ProjectCompanyService', operation },
    extra: { operation }
  });
  console.error(fullMessage, error);

  throw new Error(parsedMessage);
}

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT MEMBERSHIP QUERIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get user's role in a project
 */
export async function getUserProjectRole(
  projectId: string,
  userId?: string
): Promise<ProjectRole | null> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getUserProjectRole:', projectId, userId);
    const actor = getDemoAuthorityActor();
    const participant = getDemoProjectParticipant(projectId, userId ?? actor?.id ?? '');
    return participant?.status === 'active' ? participant.projectRole : null;
  }

  let resolvedUserId = userId;
  if (!resolvedUserId) {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) return null;
    resolvedUserId = authData.user.id;
  }

  const { data, error } = await supabase
    .from('project_memberships')
    .select('project_role')
    .eq('project_id', projectId)
    .eq('user_id', resolvedUserId)
    .eq('status', 'active')
    .lte('effective_from', new Date().toISOString())
    .or(`effective_until.is.null,effective_until.gt.${new Date().toISOString()}`)
    .maybeSingle();

  if (error) {
    console.warn('Project membership role query failed:', error);
    return null;
  }

  return (data?.project_role as ProjectRole | undefined) ?? null;
}

/**
 * Check if user is Project Manager
 */
export async function isProjectManager(
  projectId: string,
  userId?: string
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.isProjectManager:', projectId, userId);
    const actor = getDemoAuthorityActor();
    const participant = getDemoProjectParticipant(projectId, userId ?? actor?.id ?? '');
    return participant?.status === 'active' && participant.projectRole === 'Project Manager';
  }

  return (await getUserProjectRole(projectId, userId)) === 'Project Manager';
}

/**
 * Check if user is a project member
 */
export async function isProjectMember(
  projectId: string,
  userId?: string
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.isProjectMember:', projectId, userId);
    const actor = getDemoAuthorityActor();
    return getDemoProjectParticipant(projectId, userId ?? actor?.id ?? '')?.status === 'active';
  }

  return (await getUserProjectRole(projectId, userId)) !== null;
}

/**
 * Get project members with company information
 */
export async function getProjectMembersWithCompanies(
  projectId: string
): Promise<ProjectMemberCompanyInfo[]> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getProjectMembersWithCompanies:', projectId);
    return [];
  }

  const { data, error } = await supabase.rpc('get_project_members_secure', {
    p_project_id: projectId,
    p_include_inactive: false,
  });

  if (error) {
    console.warn('Governed project member query failed:', error);
    return [];
  }

  return (data || []).map((member: {
    user_id: string;
    project_role: string;
    username: string;
    email: string;
    avatar_url: string | null;
    created_at: string;
  }) => ({
    user_id: member.user_id,
    project_role: member.project_role as ProjectRole,
    is_company_representative: false,
    user_name: member.username,
    user_email: member.email,
    avatar_url: member.avatar_url ?? undefined,
    employment_type: 'external' as const,
    joined_at: member.created_at,
  }));
}

/**
 * Get all projects for a user
 */
export async function getUserProjects(
  userId?: string
): Promise<Array<{
  project_id: string;
  project_role: ProjectRole;
  company_id?: string;
  is_company_representative: boolean;
  joined_at: string;
}>> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getUserProjects:', userId);
    return [];
  }

  let resolvedUserId = userId;
  if (!resolvedUserId) {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) return [];
    resolvedUserId = authData.user.id;
  }

  const { data, error } = await supabase
    .from('project_memberships')
    .select('project_id, project_role, company_id, is_company_representative, joined_at')
    .eq('user_id', resolvedUserId)
    .eq('status', 'active')
    .lte('effective_from', new Date().toISOString())
    .or(`effective_until.is.null,effective_until.gt.${new Date().toISOString()}`);

  if (error) {
    console.warn('Governed user project query failed:', error);
    return [];
  }

  return (data || []).map((membership) => ({
    project_id: membership.project_id,
    project_role: membership.project_role as ProjectRole,
    company_id: membership.company_id ?? undefined,
    is_company_representative: membership.is_company_representative === true,
    joined_at: membership.joined_at,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// PM AUTHORITY CHECKS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if PM can add a member with given role
 */
export async function canPMAddMember(
  projectId: string,
  targetUserId: string,
  targetRole: ProjectRole
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.canPMAddMember:', projectId, targetUserId, targetRole);
    return canDemoManageProject(projectId);
  }

  const { data, error } = await supabase.rpc('ax1_can_manage_project', {
    p_project_id: projectId,
  });

  if (error) {
    console.warn('Governed project management check failed:', error);
    return false;
  }

  return data === true;
}

/**
 * Check if PM can change a member's role
 */
export async function canPMChangeRole(
  projectId: string,
  targetUserId: string,
  newRole: ProjectRole
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.canPMChangeRole:', projectId, targetUserId, newRole);
    return canDemoManageProject(projectId);
  }

  const { data, error } = await supabase.rpc('ax1_can_manage_project', {
    p_project_id: projectId,
  });

  if (error) {
    console.warn('Governed project management check failed:', error);
    return false;
  }

  return data === true;
}

/**
 * Check if PM can remove a member
 */
export async function canPMRemoveMember(
  projectId: string,
  targetUserId: string
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.canPMRemoveMember:', projectId, targetUserId);
    return canDemoManageProject(projectId);
  }

  const { data, error } = await supabase.rpc('ax1_can_manage_project', {
    p_project_id: projectId,
  });

  if (error) {
    console.warn('Governed project management check failed:', error);
    return false;
  }

  return data === true;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY REPRESENTATIVE FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if company rep can invite a colleague
 */
export async function canCompanyRepInvite(
  projectId: string,
  targetUserId: string
): Promise<boolean> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.canCompanyRepInvite:', projectId, targetUserId);
    const actor = getDemoAuthorityActor();
    if (!actor) return false;
    if (canDemoManageProject(projectId)) return true;
    const representative = getDemoCompanyRepresentatives(projectId)
      .find((item) => item.userId === actor.id);
    const colleague = getDemoProjectParticipant(projectId, targetUserId);
    return Boolean(representative && colleague?.status === 'active'
      && colleague.companyId === representative.companyId);
  }

  console.warn(
    'Company-representative colleague invitations are unavailable until a governed server contract is installed.',
    { projectId, targetUserId },
  );
  return false;
}

/**
 * Add colleague from same company to project
 */
export async function addCompanyColleague(
  projectId: string,
  colleagueUserId: string
): Promise<ColleagueInviteResult> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.addCompanyColleague:', projectId, colleagueUserId);
    if (!await canCompanyRepInvite(projectId, colleagueUserId)) {
      throw new Error('Company colleague invitation is not allowed for this demo persona.');
    }
    return {
      status: 'added',
      message: 'Colleague added to project',
      membership_id: crypto.randomUUID()
    };
  }

  throw new Error(
    'Company-representative colleague invitations are unavailable until a governed server contract is installed.',
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT COMPANIES - CRUD
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add a company to a project
 */
export async function addProjectCompany(
  data: ProjectCompanyCreate
): Promise<ProjectCompany> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.addProjectCompany:', data);
    if (!canDemoManageProject(data.project_id)) {
      throw new Error('Project company management is not allowed for this demo persona.');
    }
    return {
      id: crypto.randomUUID(),
      project_id: data.project_id,
      company_id: data.company_id,
      relationship_type: data.relationship_type,
      engagement_type: data.engagement_type || 'primary',
      default_project_role: data.default_project_role || 'Vendor',
      can_invite_colleagues: data.can_invite_colleagues || false,
      can_view_internal_content: data.can_view_internal_content || false,
      max_seats: data.max_seats,
      status: data.status || 'active',
      effective_from: data.effective_from,
      effective_to: data.effective_to,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  const { data: projectCompany, error } = await supabase
    .from('project_companies')
    .insert(data)
    .select()
    .single();

  if (error) handleError('addProjectCompany', error);
  return projectCompany as ProjectCompany;
}

/**
 * Update a project company
 */
export async function updateProjectCompany(
  projectCompanyId: string,
  updates: ProjectCompanyUpdate
): Promise<ProjectCompany> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.updateProjectCompany:', projectCompanyId, updates);
    throw new Error('Demo cannot update a project-company link without its project scope.');
  }

  const { data, error } = await supabase
    .from('project_companies')
    .update(updates)
    .eq('id', projectCompanyId)
    .select()
    .single();

  if (error) handleError('updateProjectCompany', error);
  return data as ProjectCompany;
}

/**
 * Remove a company from a project (set status to completed)
 */
export async function removeProjectCompany(
  projectCompanyId: string
): Promise<void> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.removeProjectCompany:', projectCompanyId);
    throw new Error('Demo cannot remove a project-company link without its project scope.');
  }

  const { error } = await supabase
    .from('project_companies')
    .update({
      status: 'completed',
      effective_to: new Date().toISOString().split('T')[0]
    })
    .eq('id', projectCompanyId);

  if (error) handleError('removeProjectCompany', error);
}

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT COMPANIES - QUERIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all companies for a project
 */
export async function getProjectCompanies(
  projectId: string,
  options?: { includeInactive?: boolean }
): Promise<ProjectCompanyWithDetails[]> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getProjectCompanies:', projectId, options);
    return [];
  }

  let query = supabase
    .from('project_companies')
    .select(`
      *,
      company:companies (
        id,
        name,
        logo_url,
        company_type
      )
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (!options?.includeInactive) {
    query = query.eq('status', 'active');
  }

  const { data, error } = await query;

  if (error) handleError('getProjectCompanies', error);

  // Add member count
  const result = (data as ProjectCompanyWithDetails[]) || [];

  // Get member counts per company
  const { data: memberCounts } = await supabase
    .from('project_memberships')
    .select('company_id')
    .eq('project_id', projectId)
    .eq('status', 'active')
    .not('company_id', 'is', null);

  if (memberCounts) {
    const countMap = new Map<string, number>();
    for (const m of memberCounts) {
      const cid = m.company_id as string;
      countMap.set(cid, (countMap.get(cid) || 0) + 1);
    }
    for (const pc of result) {
      pc.member_count = countMap.get(pc.company_id) || 0;
    }
  }

  return result;
}

/**
 * Get a specific project company
 */
export async function getProjectCompany(
  projectId: string,
  companyId: string
): Promise<ProjectCompany | null> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getProjectCompany:', projectId, companyId);
    return null;
  }

  const { data, error } = await supabase
    .from('project_companies')
    .select('*')
    .eq('project_id', projectId)
    .eq('company_id', companyId)
    .eq('status', 'active')
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    handleError('getProjectCompany', error);
  }

  if (!data) return null;

  const raw = data as Omit<ProjectCompany, 'relationship_type'> & {
    relationship_type: LegacyProjectCompanyRelationshipValue;
  };

  return {
    ...raw,
    relationship_type: normalizeProjectCompanyRelationship(raw.relationship_type),
  };
}

/**
 * Get all projects a company is involved in
 */
export async function getCompanyProjects(
  companyId: string
): Promise<Array<{
  project_id: string;
  relationship_type: ProjectCompanyRelationshipType;
  engagement_type: string;
  status: string;
}>> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getCompanyProjects:', companyId);
    return [];
  }

  const { data, error } = await supabase
    .from('project_companies')
    .select('project_id, relationship_type, engagement_type, status')
    .eq('company_id', companyId)
    .eq('status', 'active');

  if (error) handleError('getCompanyProjects', error);

  return (data || []).map((row) => ({
    ...row,
    relationship_type: normalizeProjectCompanyRelationship(
      row.relationship_type as LegacyProjectCompanyRelationshipValue,
    ),
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// MEMBER COMPANY ASSIGNMENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update a member's company assignment on a project
 */
export async function updateMemberCompany(
  membershipId: string,
  companyId: string | null,
  isCompanyRepresentative: boolean = false
): Promise<void> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.updateMemberCompany:', membershipId, companyId);
    throw new Error('Demo cannot update a member company assignment without its project scope.');
  }

  const { error } = await supabase
    .from('project_memberships')
    .update({
      company_id: companyId,
      is_company_representative: isCompanyRepresentative
    })
    .eq('id', membershipId);

  if (error) handleError('updateMemberCompany', error);
}

/**
 * Make a member a company representative
 */
export async function setCompanyRepresentative(
  projectId: string,
  userId: string,
  isRepresentative: boolean
): Promise<void> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.setCompanyRepresentative:', projectId, userId, isRepresentative);
    if (!canDemoManageProject(projectId)) {
      throw new Error('Company representative management is not allowed for this demo persona.');
    }
    return;
  }

  const { error } = await supabase
    .from('project_memberships')
    .update({ is_company_representative: isRepresentative })
    .eq('project_id', projectId)
    .eq('user_id', userId)
    .eq('status', 'active');

  if (error) handleError('setCompanyRepresentative', error);
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT DEFAULT OBJECT
// ─────────────────────────────────────────────────────────────────────────────

export const ProjectCompanyService = {
  // Membership queries
  getUserProjectRole,
  isProjectManager,
  isProjectMember,
  getProjectMembersWithCompanies,
  getUserProjects,

  // PM authority checks
  canPMAddMember,
  canPMChangeRole,
  canPMRemoveMember,

  // Company rep functions
  canCompanyRepInvite,
  addCompanyColleague,

  // Project companies CRUD
  addProjectCompany,
  updateProjectCompany,
  removeProjectCompany,
  getProjectCompanies,
  getProjectCompany,
  getCompanyProjects,

  // Member company assignment
  updateMemberCompany,
  setCompanyRepresentative,

  // Project participant company list
  getProjectCompaniesForUser,
};

export default ProjectCompanyService;

// ─────────────────────────────────────────────────────────────────────────────
// PROJECT PARTICIPANT COMPANY LIST
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Raw project-company row returned by the canonical RLS-protected relation.
 */
interface ProjectCompanyForUserRaw {
  id: string;
  company_id: string;
  relationship_type: LegacyProjectCompanyRelationshipValue;
  status: string;
  is_primary: boolean;
  companies:
    | {
        name: string;
        website: string | null;
        country: string | null;
        company_type: string | null;
        industry: string | null;
        logo_url: string | null;
        updated_at: string | null;
      }
    | Array<{
        name: string;
        website: string | null;
        country: string | null;
        company_type: string | null;
        industry: string | null;
        logo_url: string | null;
        updated_at: string | null;
      }>
    | null;
}

export interface LinkProjectCompanyInput {
  projectId: string;
  projectName?: string;
  companyId: string;
  relationshipType: ProjectCompanyRelationshipType;
  isPrimary?: boolean;
}

function toProjectCompanyStorageRelationship(
  relationshipType: ProjectCompanyRelationshipType,
): LegacyProjectCompanyRelationshipValue {
  // The current database constraint still stores the historical `client`
  // value. Keep this compatibility translation at the persistence boundary;
  // all UI and domain code continues to use `capital_recipient`.
  return relationshipType === 'capital_recipient' ? 'client' : relationshipType;
}

/**
 * Link an existing workspace company to a project using the canonical
 * project_companies persistence model. RLS remains the authority boundary.
 */
export async function linkProjectCompany({
  projectId,
  projectName,
  companyId,
  relationshipType,
  isPrimary = false,
}: LinkProjectCompanyInput): Promise<{ id: string }> {
  if (IS_DEMO_MODE) {
    if (!canDemoManageProject(projectId)) throw new Error('Project participation management is not allowed.');
    const link = DemoStore.addProjectCompanyLink({
      projectId,
      projectName: projectName || `Project ${projectId}`,
      companyId,
      relationshipType: relationshipType as 'internal' | 'capital_recipient' | 'partner' | 'investor' | 'vendor',
      engagementType: isPrimary ? 'primary' : 'secondary',
    });
    return { id: link.id };
  }

  const { data, error } = await supabase
    .from('project_companies')
    .upsert(
      {
        project_id: projectId,
        company_id: companyId,
        relationship_type: toProjectCompanyStorageRelationship(relationshipType),
        status: 'active',
        is_primary: isPrimary,
      },
      { onConflict: 'project_id,company_id,relationship_type' },
    )
    .select('id')
    .single();

  if (error) handleError('linkProjectCompany', error);
  if (!data || typeof data.id !== 'string' || !data.id.trim()) throw new Error('The project company link could not be confirmed.');
  return { id: data.id };
}

/**
 * Hydrated DTO with canonical relationship type.
 * Safe for all normal callers (components, hooks, services).
 */
export interface ProjectCompanyForUser {
  link_id: string;
  company_id: string;
  company_name: string;
  relationship_type: ProjectCompanyRelationshipType;
  engagement_type: 'primary' | 'secondary';
  status: string;
  website: string | null;
  country: string | null;
  company_type: string | null;
  industry: string | null;
  logo_url: string | null;
  updated_at: string | null;
  can_view_full_details: boolean;
}

/**
 * Normalize RPC response at the persistence boundary.
 * This is the ONLY place where we read the legacy 'client' value.
 * After this, all code uses the canonical type.
 */
function hydrateProjectCompanyForUser(raw: ProjectCompanyForUserRaw): ProjectCompanyForUser {
  const company = Array.isArray(raw.companies) ? raw.companies[0] : raw.companies;

  return {
    link_id: raw.id,
    company_id: raw.company_id,
    company_name: company?.name ?? 'Unknown company',
    relationship_type: normalizeProjectCompanyRelationship(raw.relationship_type),
    engagement_type: raw.is_primary ? 'primary' : 'secondary',
    status: raw.status,
    website: company?.website ?? null,
    country: company?.country ?? null,
    company_type: company?.company_type ?? null,
    industry: company?.industry ?? null,
    logo_url: company?.logo_url ?? null,
    updated_at: company?.updated_at ?? null,
    can_view_full_details: true,
  };
}

/**
 * Get companies linked to a project.
 *
 * Authorization is enforced by the canonical `project_companies_select` RLS
 * policy (`ax1_can_view_project(project_id)`). The browser does not submit a
 * user id for authorization and no legacy visibility columns are queried.
 * 
 * @param projectId - The project ID
 * @param userId - The user ID requesting the data
 * @returns Array of visible companies with appropriate detail levels
 */
export async function getProjectCompaniesForUser(
  projectId: string,
  userId: string
): Promise<ProjectCompanyForUser[]> {
  if (IS_DEMO_MODE) {
    console.log('[DEMO] ProjectCompanyService.getProjectCompaniesForUser:', projectId, userId);
    // In demo mode, return mock data - this will be replaced by caller
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('project_companies')
      .select(`
        id,
        company_id,
        relationship_type,
        status,
        is_primary,
        companies!inner (
          name,
          website,
          country,
          company_type,
          industry,
          logo_url,
          updated_at
        )
      `)
      .eq('project_id', projectId)
      .eq('status', 'active')
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) {
      console.error('project_companies query failed:', error);
      throw new Error(parseError(error));
    }

    return ((data as ProjectCompanyForUserRaw[]) || []).map(hydrateProjectCompanyForUser);
  } catch (err) {
    captureError(err as Error, {
      tags: { service: 'ProjectCompanyService', operation: 'getProjectCompaniesForUser' },
      extra: { projectId, userId }
    });
    throw err;
  }
}
